import { clsx } from 'clsx';
import { useMobile } from 'common-nextjs';
import React, { useEffect, useState } from 'react';
import { Button } from 'ui';
import ShopLink from '~/components/Links/ShopLink';
import HorizontalOnlyUnlimitedTile from '~/components/legos/Tiles/HorizontalOnlyUnlimitedTile';
import useFacetFiltering from '~/contexts/facetFiltering/useFacetFiltering';
import { TypedRailsFacet } from '~/typings/services/rails/facets';
import { RailsCategory } from '~/typings/services/rails/item';

/**
 * This component replaced ./NestedCategoryPicker. We decided to not go with
 * the complex nested selector, but we may revisit it later.
 */

interface Props {
  className?: string;
  heading?: string;
  showImages?: boolean;
}

const SimpleCategoryPicker: React.FC<Props> = ({
  className,
  heading,
  showImages = true,
}) => {
  const {
    categories,
    category,
    changeFacetSelection,
    facets,
    lander,
    transferableLanderQueryAndFilterState,
  } = useFacetFiltering();
  const [viewMore, setViewMore] = useState(false);
  const mobile = useMobile();

  const max = mobile ? 6 : 9;

  useEffect(() => {
    setViewMore(false);
  }, [category?.id]);

  function evaluateChildren() {
    let children: TypedRailsFacet<RailsCategory>[] | undefined;

    if (category) {
      if (category.has_children && category.children?.length! > 0) {
        children = category.children;
      }
    } else if (categories) {
      children = categories;
    } else if (lander) {
      children = categories;
    }

    if (!children || children.length === 0) {
      return null;
    }

    return children;
  }

  const [children, setChildren] = useState(evaluateChildren());

  useEffect(() => {
    setChildren(evaluateChildren());
  }, [facets]);

  if (!children) {
    return null;
  }

  return (
    <section className="container">
      {heading && <div className="mb-2 text-lg font-semibold">{heading}</div>}

      <div
        className={clsx(
          'grid grid-cols-1 gap-2 md:grid-cols-3 md:gap-4',
          className,
        )}
      >
        {children?.map((c, i) => {
          if (lander && c.url) {
            return (
              <ShopLink
                key={c.id}
                path={c.url}
                query={transferableLanderQueryAndFilterState}
                className={i >= max && !viewMore ? 'hidden' : undefined}
              >
                <HorizontalOnlyUnlimitedTile
                  image={
                    showImages
                      ? c.image_url || c.primary_image?.thumb_url || ''
                      : undefined
                  }
                  title={c.name}
                />
              </ShopLink>
            );
          } else {
            return (
              <HorizontalOnlyUnlimitedTile
                key={c.id}
                className={i >= max && !viewMore ? 'hidden' : undefined}
                image={
                  showImages
                    ? c.image_url || c.primary_image?.thumb_url || ''
                    : undefined
                }
                title={c.name}
                onClick={() =>
                  changeFacetSelection('category', [c.id], false, true)
                }
              />
            );
          }
        })}

        {children?.length! >= max && (
          <Button
            variant="outlined"
            color="black"
            className="md:col-span-3"
            onClick={() => setViewMore(!viewMore)}
          >
            View {viewMore ? 'Less' : 'All'}
          </Button>
        )}
      </div>
    </section>
  );
};

export default SimpleCategoryPicker;
